/**
 * ScheduleSettingsDrawer Component
 * Configure schedule settings: working days, hours, holidays, defaults
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Settings,
  X,
  Calendar,
  Clock,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
  RefreshCw,
} from 'lucide-react';
import { Dropdown, NumberInput, DatePicker } from '../forms';
import type { ViewMode } from '../../types/schedule';
import {
  useGetScheduleSettingsQuery,
  useUpdateScheduleSettingsMutation,
} from '../../services/scheduleApi';

export interface ScheduleSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

// Validation schema
const settingsSchema = yup.object({
  workingDays: yup
    .array()
    .of(yup.number().min(0).max(6))
    .min(1, 'At least one working day is required'),
  workingHoursStart: yup.string().required('Start time is required'),
  workingHoursEnd: yup
    .string()
    .required('End time is required')
    .test('is-after-start', 'End time must be after start time', function (value) {
      const { workingHoursStart } = this.parent;
      if (!workingHoursStart || !value) return true;
      // String comparison works for HH:MM format (e.g., "17:00" > "08:00")
      return value > workingHoursStart;
    }),
  defaultTaskDuration: yup
    .number()
    .min(1, 'Duration must be at least 1 day')
    .max(365, 'Duration cannot exceed 365 days')
    .required('Default duration is required'),
  autoCalculate: yup.boolean(),
  defaultViewMode: yup.string().oneOf(['day', 'week', 'month', 'quarter']).required(),
  showWeekends: yup.boolean(),
  showCriticalPath: yup.boolean(),
});

type FormData = yup.InferType<typeof settingsSchema>;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

const VIEW_MODE_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return { value: `${hour}:00`, label: `${hour}:00` };
});

const ScheduleSettingsDrawer: React.FC<ScheduleSettingsDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const [holidays, setHolidays] = useState<string[]>([]);
  const [newHoliday, setNewHoliday] = useState<string>('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch current settings
  const {
    data: settingsResponse,
    isLoading: isLoadingSettings,
    refetch,
  } = useGetScheduleSettingsQuery({ projectId }, { skip: !projectId || !isOpen });

  const [updateSettings, { isLoading: isSaving }] = useUpdateScheduleSettingsMutation();

  const currentSettings = settingsResponse?.data;

  // Default form values
  const defaultValues: FormData = useMemo(
    () => ({
      workingDays: [1, 2, 3, 4, 5], // Mon-Fri
      workingHoursStart: '08:00',
      workingHoursEnd: '17:00',
      defaultTaskDuration: 5,
      autoCalculate: true,
      defaultViewMode: 'week' as ViewMode,
      showWeekends: true,
      showCriticalPath: true,
    }),
    []
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(settingsSchema) as any,
    defaultValues,
  });

  const workingDays = watch('workingDays');

  // Initialize form with current settings
  useEffect(() => {
    if (currentSettings) {
      reset({
        workingDays: currentSettings.workingDays || [1, 2, 3, 4, 5],
        workingHoursStart: currentSettings.workingHours?.start || '08:00',
        workingHoursEnd: currentSettings.workingHours?.end || '17:00',
        defaultTaskDuration: currentSettings.defaultTaskDuration || 5,
        autoCalculate: currentSettings.autoCalculate ?? true,
        defaultViewMode: currentSettings.defaultViewMode || 'week',
        showWeekends: currentSettings.showWeekends ?? true,
        showCriticalPath: currentSettings.showCriticalPath ?? true,
      });
      setHolidays(
        (currentSettings.holidays || []).map((h: Date | string) =>
          typeof h === 'string' ? h.split('T')[0] : new Date(h).toISOString().split('T')[0]
        )
      );
    }
  }, [currentSettings, reset]);

  // Toggle working day
  const toggleWorkingDay = useCallback(
    (day: number) => {
      const current = workingDays || [];
      if (current.includes(day)) {
        // Prevent removing the last working day
        if (current.length <= 1) return;
        setValue(
          'workingDays',
          current.filter(d => d !== day),
          { shouldDirty: true }
        );
      } else {
        setValue('workingDays', [...current, day].sort(), { shouldDirty: true });
      }
    },
    [workingDays, setValue]
  );

  // Add holiday
  const addHoliday = useCallback(() => {
    if (newHoliday && !holidays.includes(newHoliday)) {
      setHolidays(prev => [...prev, newHoliday].sort());
      setNewHoliday('');
    }
  }, [newHoliday, holidays]);

  // Remove holiday
  const removeHoliday = useCallback((date: string) => {
    setHolidays(prev => prev.filter(h => h !== date));
  }, []);

  // Submit handler
  const onSubmit = async (data: FormData) => {
    try {
      await updateSettings({
        projectId,
        data: {
          workingDays: data.workingDays as number[],
          workingHours: {
            start: data.workingHoursStart as string,
            end: data.workingHoursEnd as string,
          },
          holidays,
          defaultTaskDuration: data.defaultTaskDuration as number,
          autoCalculate: data.autoCalculate as boolean,
          defaultViewMode: data.defaultViewMode as ViewMode,
          showWeekends: data.showWeekends as boolean,
          showCriticalPath: data.showCriticalPath as boolean,
        },
      }).unwrap();

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      // Error handling is done by RTK Query
    }
  };

  // Format date for display
  const formatHolidayDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] max-w-2xl animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <Settings className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Schedule Settings</h2>
                <p className="text-sm text-gray-500">Configure working calendar and defaults</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => refetch()}
                disabled={isLoadingSettings}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh settings"
              >
                <RefreshCw className={`h-4 w-4 ${isLoadingSettings ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {isLoadingSettings ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                {/* Success Message */}
                {saveSuccess && (
                  <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    <span className="text-sm font-medium">Settings saved successfully</span>
                  </div>
                )}

                {/* Working Days */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Working Days
                    </h3>
                  </div>

                  <div className="grid grid-cols-7 gap-2">
                    {DAYS_OF_WEEK.map(day => {
                      const isSelected = (workingDays || []).includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleWorkingDay(day.value)}
                          className={`
                            flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all
                            ${
                              isSelected
                                ? 'border-red-500 bg-red-50 text-red-700'
                                : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                            }
                          `}
                        >
                          <span className="text-xs font-medium">{day.short}</span>
                        </button>
                      );
                    })}
                  </div>

                  {errors.workingDays && (
                    <p className="text-sm text-red-600 flex items-center space-x-1">
                      <AlertCircle className="h-4 w-4" />
                      <span>{errors.workingDays.message}</span>
                    </p>
                  )}
                </div>

                {/* Working Hours */}
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Working Hours
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Controller
                      name="workingHoursStart"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          label="Start Time"
                          value={field.value || '08:00'}
                          options={TIME_OPTIONS}
                          onChange={field.onChange}
                          required
                        />
                      )}
                    />

                    <Controller
                      name="workingHoursEnd"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          label="End Time"
                          value={field.value || '17:00'}
                          options={TIME_OPTIONS}
                          onChange={field.onChange}
                          required
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Holidays */}
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                        Holidays
                      </h3>
                    </div>
                    <span className="text-xs text-gray-500">{holidays.length} holidays</span>
                  </div>

                  {/* Add Holiday */}
                  <div className="flex items-end space-x-2">
                    <div className="flex-1">
                      <DatePicker
                        label="Add Holiday"
                        value={newHoliday}
                        onChange={date => setNewHoliday(date || '')}
                        placeholder="Select date..."
                      />
                    </div>
                    <button
                      type="button"
                      onClick={addHoliday}
                      disabled={!newHoliday}
                      className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Holiday List */}
                  {holidays.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {holidays.map(date => (
                        <div
                          key={date}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <span className="text-sm text-gray-700">{formatHolidayDate(date)}</span>
                          <button
                            type="button"
                            onClick={() => removeHoliday(date)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No holidays configured</p>
                  )}
                </div>

                {/* Default Settings */}
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Default Settings
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <Controller
                      name="defaultTaskDuration"
                      control={control}
                      render={({ field }) => (
                        <NumberInput
                          label="Default Task Duration (Days)"
                          value={field.value || 5}
                          onChange={field.onChange}
                          min={1}
                          max={365}
                        />
                      )}
                    />

                    <Controller
                      name="defaultViewMode"
                      control={control}
                      render={({ field }) => (
                        <Dropdown
                          label="Default View Mode"
                          value={field.value || 'week'}
                          options={VIEW_MODE_OPTIONS}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Display Options */}
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Display Options
                  </h3>

                  <div className="space-y-3">
                    <Controller
                      name="autoCalculate"
                      control={control}
                      render={({ field }) => (
                        <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              Auto-calculate Dates
                            </span>
                            <p className="text-xs text-gray-500">
                              Automatically recalculate dates based on dependencies
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={field.value || false}
                            onChange={field.onChange}
                            className="h-5 w-5 rounded border-gray-300 text-red-500 focus:ring-red-500"
                          />
                        </label>
                      )}
                    />

                    <Controller
                      name="showWeekends"
                      control={control}
                      render={({ field }) => (
                        <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                          <div>
                            <span className="text-sm font-medium text-gray-900">Show Weekends</span>
                            <p className="text-xs text-gray-500">
                              Display weekend columns in Gantt chart
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={field.value || false}
                            onChange={field.onChange}
                            className="h-5 w-5 rounded border-gray-300 text-red-500 focus:ring-red-500"
                          />
                        </label>
                      )}
                    />

                    <Controller
                      name="showCriticalPath"
                      control={control}
                      render={({ field }) => (
                        <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              Highlight Critical Path
                            </span>
                            <p className="text-xs text-gray-500">
                              Visually highlight critical path tasks
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={field.value || false}
                            onChange={field.onChange}
                            className="h-5 w-5 rounded border-gray-300 text-red-500 focus:ring-red-500"
                          />
                        </label>
                      )}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={
                  isSaving ||
                  (!isDirty && holidays.length === (currentSettings?.holidays?.length || 0))
                }
                className="flex items-center rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
              >
                {isSaving && (
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ScheduleSettingsDrawer;
